import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * CUSTOM HOOK: useNetworkResilience
 *
 * Provides comprehensive network resilience features:
 * - Real-time network status monitoring
 * - Automatic retry with exponential backoff
 * - Request queuing for offline operations
 * - Connection quality detection
 * - Automatic reconnection on network restore
 *
 * Fixes: Network disconnection issues, slow loading, connection timeouts
 */

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  effectiveType: string;
  downlink: number;
  rtt: number;
}

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

interface QueuedRequest {
  id: string;
  fn: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timestamp: number;
  retries: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
};

export const useNetworkResilience = (customConfig?: Partial<RetryConfig>) => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isSlowConnection: false,
    effectiveType: 'unknown',
    downlink: 0,
    rtt: 0,
  });

  const [isPending, setIsPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const config = { ...DEFAULT_RETRY_CONFIG, ...customConfig };
  const requestQueueRef = useRef<QueuedRequest[]>([]);
  const processingRef = useRef(false);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Update network status
  const updateNetworkStatus = useCallback(() => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

    const status: NetworkStatus = {
      isOnline: navigator.onLine,
      isSlowConnection: false,
      effectiveType: 'unknown',
      downlink: 0,
      rtt: 0,
    };

    if (connection) {
      status.effectiveType = connection.effectiveType || 'unknown';
      status.downlink = connection.downlink || 0;
      status.rtt = connection.rtt || 0;
      status.isSlowConnection = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
    }

    setNetworkStatus(status);

    console.log('[Network Resilience] Status updated:', status);

    return status;
  }, []);

  // Process queued requests
  const processQueue = useCallback(async () => {
    if (processingRef.current || !networkStatus.isOnline || requestQueueRef.current.length === 0) {
      return;
    }

    processingRef.current = true;
    setIsPending(true);

    while (requestQueueRef.current.length > 0 && networkStatus.isOnline) {
      const request = requestQueueRef.current[0];

      try {
        console.log(`[Network Resilience] Processing queued request ${request.id} (retry ${request.retries}/${config.maxRetries})`);

        const result = await request.fn();
        request.resolve(result);
        requestQueueRef.current.shift();
        setPendingCount(requestQueueRef.current.length);

      } catch (error) {
        request.retries++;

        if (request.retries >= config.maxRetries) {
          console.error(`[Network Resilience] Request ${request.id} failed after ${config.maxRetries} retries`);
          request.reject(error);
          requestQueueRef.current.shift();
        } else {
          const delay = Math.min(
            config.baseDelay * Math.pow(config.backoffMultiplier, request.retries),
            config.maxDelay
          );

          console.log(`[Network Resilience] Request ${request.id} failed, retrying in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        setPendingCount(requestQueueRef.current.length);
      }
    }

    processingRef.current = false;
    setIsPending(requestQueueRef.current.length > 0);
  }, [networkStatus.isOnline, config]);

  // Resilient fetch wrapper with automatic retry and queue
  const resilientFetch = useCallback(async <T,>(
    fn: () => Promise<T>,
    options?: { timeout?: number; priority?: 'high' | 'normal' | 'low'; cancelable?: boolean }
  ): Promise<T> => {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timeout = options?.timeout || 30000; // Default 30s timeout (increased from 8s)
    const cancelable = options?.cancelable !== false;

    // Create abort controller for this request
    const abortController = new AbortController();
    if (cancelable) {
      abortControllersRef.current.set(requestId, abortController);
    }

    // If offline, queue the request
    if (!networkStatus.isOnline) {
      console.log(`[Network Resilience] Offline - queuing request ${requestId}`);

      return new Promise((resolve, reject) => {
        const queuedRequest: QueuedRequest = {
          id: requestId,
          fn,
          resolve,
          reject,
          timestamp: Date.now(),
          retries: 0,
        };

        // Add to queue based on priority
        if (options?.priority === 'high') {
          requestQueueRef.current.unshift(queuedRequest);
        } else {
          requestQueueRef.current.push(queuedRequest);
        }

        setPendingCount(requestQueueRef.current.length);
      });
    }

    // Online - execute with timeout and retry
    let retries = 0;

    while (retries <= config.maxRetries) {
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error(`Request timeout after ${timeout}ms`));
          }, timeout);
        });

        const result = await Promise.race([
          fn(),
          timeoutPromise,
        ]);

        // Cleanup abort controller
        if (cancelable) {
          abortControllersRef.current.delete(requestId);
        }

        return result;

      } catch (error) {
        retries++;

        // Check if request was aborted
        if (abortController.signal.aborted) {
          console.log(`[Network Resilience] Request ${requestId} was cancelled`);
          throw new Error('Request cancelled');
        }

        if (retries > config.maxRetries) {
          console.error(`[Network Resilience] Request ${requestId} failed after ${config.maxRetries} retries:`, error);

          // Cleanup abort controller
          if (cancelable) {
            abortControllersRef.current.delete(requestId);
          }

          throw error;
        }

        const delay = Math.min(
          config.baseDelay * Math.pow(config.backoffMultiplier, retries - 1),
          config.maxDelay
        );

        console.log(`[Network Resilience] Request ${requestId} failed (attempt ${retries}/${config.maxRetries}), retrying in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw new Error('Max retries exceeded');
  }, [networkStatus.isOnline, config]);

  // Cancel specific request or all requests
  const cancelRequests = useCallback((requestId?: string) => {
    if (requestId) {
      const controller = abortControllersRef.current.get(requestId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(requestId);
        console.log(`[Network Resilience] Cancelled request ${requestId}`);
      }
    } else {
      // Cancel all requests
      abortControllersRef.current.forEach((controller) => {
        controller.abort();
      });
      abortControllersRef.current.clear();
      console.log('[Network Resilience] Cancelled all requests');
    }
  }, []);

  // Clear the queue
  const clearQueue = useCallback(() => {
    requestQueueRef.current.forEach(req => {
      req.reject(new Error('Queue cleared'));
    });
    requestQueueRef.current = [];
    setPendingCount(0);
    console.log('[Network Resilience] Queue cleared');
  }, []);

  // Setup event listeners for network status
  useEffect(() => {
    console.log('[Network Resilience] Initializing');

    const handleOnline = () => {
      console.log('[Network Resilience] Connection restored');
      updateNetworkStatus();
      processQueue();
    };

    const handleOffline = () => {
      console.log('[Network Resilience] Connection lost');
      updateNetworkStatus();
    };

    const handleConnectionChange = () => {
      updateNetworkStatus();
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Initial status update
    updateNetworkStatus();

    // Cleanup function
    return () => {
      console.log('[Network Resilience] Cleaning up');
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }

      // Cancel all pending requests
      cancelRequests();

      // Clear queue
      requestQueueRef.current = [];
    };
  }, [updateNetworkStatus, processQueue, cancelRequests]);

  // Try to process queue when it changes
  useEffect(() => {
    if (networkStatus.isOnline && requestQueueRef.current.length > 0) {
      processQueue();
    }
  }, [networkStatus.isOnline, processQueue]);

  return {
    networkStatus,
    isPending,
    pendingCount,
    resilientFetch,
    cancelRequests,
    clearQueue,
    updateNetworkStatus,
  };
};
